import RightArrowSvg from 'components/RightArrowSvg'
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useApi } from '../providers/ApiProvider'
import SidebarLayout from 'components/SidebarLayout'
import { StyleUtil } from 'utils/StyleUtil'
import ButtonSpinner from 'views/ButtonSpinner'
import { GeneratedQuestion } from 'apis/entities/quiz.entity'
import { ToastUtil } from 'utils/ToastUtil'
import { resolveRelativeUrls } from 'utils/HtmlUtil'
import QuestionEditViewForAuthoring from 'views/QuestionEditViewForAuthoring'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import {
  storeGeneratedQuestions,
  deleteGeneratedQuestions,
} from 'app/QuestionsSlice'
import {
  DocumentCheckerResponse,
  LanguageToolMatch,
} from 'apis/entities/document.entity'
import wordSvg from '../images/word-doc.svg'
import Spinner from '../views/Spinner'
import { Role, useCompositeAuth } from 'providers/CompositeAuthProvider'
import { Switch } from 'components/Switch'
import WidgetBulkUploadTopics from 'views/WidgetBulkUploadTopics'

function AuthoringPage() {
  const {
    publish,
    upload,
    downloadDocument,
    downloadIntV1AsWord,
    viewIntV1Json,
    getDocumentHtml,
    updateDocumentByGeneratedQuestions,
    documentChecker,
    generateQuestions,
  } = useApi()
  const { user, getRole } = useCompositeAuth()
  const role = getRole()
  const canPublishAuthoring =
    role === Role.SuperAdmin || role === Role.Admin || role === Role.Author
  const [theFile, setTheFile] = useState<File>()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [publishUrl, setPublishUrl] = useState<string | null>(null)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [uploadingTheme, setUploadingTheme] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const [isGenerated, setIsGenerated] = useState<boolean>(false)
  const [isPublishing, setIsPublishing] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isPublished, setIsPublished] = useState<boolean>(false)
  const [documentError, setDocumentError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const isDryRun: boolean = process.env.REACT_APP_DRY_RUN === 'true'
  const [questions, setQuestions] = useState<GeneratedQuestion[]>([])
  const [showQuestions, setShowQuestions] = useState<boolean>(false)
  const generatedQuestions = useAppSelector(
    (state) => state.questions.generatedQuestions,
  )
  const dispatch = useAppDispatch()

  const [spellCheck, setSpellCheck] = useState<DocumentCheckerResponse | null>(
    null,
  )
  const [isChecking, setIsChecking] = useState<boolean>(false)
  const [isChecked, setIsChecked] = useState<boolean>(false)

  const [mentemOrUnswPage, setMentemOrUnswPage] = useState<
    'mentem' | 'unsw' | null
  >(null)
  const [uploadVersion, setUploadVersion] = useState<string | undefined>()

  const [mentemOrUnswBulkPage, setMentemOrUnswBulkPage] = useState<
    'mentem' | 'unsw' | null
  >(null)
  const [bulkUploadVersion, setBulkUploadVersion] = useState<
    string | undefined
  >()

  const [isDarkMode, setIsDarkMode] = useState<boolean>(false)

  useEffect(() => {
    const iframe = document.getElementById('content') as HTMLIFrameElement
    if (iframe) {
      iframe.contentWindow?.postMessage(
        { type: 'set-color-mode', data: isDarkMode ? 'dark' : 'light' },
        '*',
      )
    }
  }, [isDarkMode])

  const scrollToHighlight = (id: number) => {
    const iframe = document.getElementById('content') as HTMLIFrameElement
    if (iframe) {
      iframe.contentWindow?.postMessage(
        { type: 'scroll-to-highlight', data: `highlighted-${id}` },
        '*',
      )
    }
  }

  const checkDocument = useCallback(async () => {
    if (!documentId) return
    setIsChecking(true)
    try {
      const result = await documentChecker(documentId)
      if (
        result &&
        result.spelling_items &&
        result.grammar_items &&
        (result.spelling_items.length > 0 || result.grammar_items.length > 0)
      ) {
        setSpellCheck(result)

        // pass data to iframe
        const iframe = document.getElementById('content') as HTMLIFrameElement
        if (iframe) {
          // join spelling and grammar items
          const items = result.spelling_items.concat(result.grammar_items)
          // const words = items.map((item) => item.incorrect)
          iframe.contentWindow?.postMessage(
            { type: 'document-checker', data: items },
            '*',
          )
        }
      } else {
        ToastUtil.error('No spelling or grammar errors found')
      }
    } catch (e) {
      console.error(e)
      ToastUtil.error('Failed to check document')
    } finally {
      setIsChecking(false)
    }
  }, [documentChecker, documentId])

  const onCheck = async () => {
    try {
      setIsChecking(true)
      setSpellCheck(null)
      await checkDocument()
      setIsChecked(true)
    } catch (e) {
      console.error(e)
      ToastUtil.error('Failed to check document')
    } finally {
      setIsChecking(false)
    }
  }

  const handlePublish = async () => {
    if (!documentId || isPublishing || isPublished) return
    try {
      setIsPublishing(true)
      const data = await publish(documentId)
      console.log(data)
      setIsPublishing(false)
      const success = data['success'] as boolean
      if (success) {
        setPublishUrl(data['publishUrl'])
        setIsPublished(true)
        ToastUtil.success('Published!')
      } else {
        ToastUtil.error('Failed to publish!')
      }
    } catch (err) {
      console.error(err)
      setIsPublishing(false)
      setIsPublished(false)
    } finally {
      if (isDryRun) {
        setPublishUrl('https://www.google.com')
        setIsPublished(true)
      }
    }
  }

  const handleUpload = async (theme: string) => {
    console.log(theFile)
    setUploadingTheme(theme)
    console.log(user)

    if (!theFile || isUploading) return
    let formData = new FormData()
    formData.append('file', theFile)
    formData.append('userId', user?.id || '')
    formData.append('username', user?.username || '')
    formData.append('theme', theme)
    if (uploadVersion) {
      formData.append('version', uploadVersion)
    }

    try {
      // reset iframe
      setPreviewUrl(null)
      setDownloadUrl(null)
      setPublishUrl(null)
      setDocumentId(null)
      setDocumentError(null)
      // reset states
      setIsPublished(false)
      setIsUploading(true)
      setIsGenerating(false)
      setIsGenerated(false)
      setIsChecking(false)
      setIsChecked(false)
      setSpellCheck(null)
      setIsSaving(false)
      setQuestions([])
      setShowQuestions(false)
      const data = await upload(formData)
      console.log(data)
      setIsUploading(false)
      setPreviewUrl(data['previewUrl'])
      setDownloadUrl(data['downloadUrl'])
      setDocumentId(data['id'])

      // handleUpload(theme)
    } catch (err) {
      console.error(err)
      setIsUploading(false)
      const message = (err as any).message
      if (message) {
        // document format error
        setDocumentError(message.replaceAll('\n', '<br/>'))
      } else {
        // fallback general error
        setDocumentError(`Failed to upload!<br/>${err}`)
      }
    } finally {
      if (isDryRun) {
        setPreviewUrl('https://www.google.com')
        setDownloadUrl('https://www.google.com')
        setDocumentId('test_id')
      }
    }
  }

  const hasFiles = (e: React.DragEvent<HTMLDivElement>) => {
    const types = e.dataTransfer.types
    return types.indexOf('Files') > -1
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return
    }
    let tmpFile = e.target.files[0] as any
    if (!tmpFile) return
    // tmpFile['originalname'] = tmpFile.name
    setTheFile(tmpFile)
  }

  const copyEmbedCode = () => {
    const title = 'Course'
    const url = getPreviewOrPublishUrl()
    const code = `<iframe width="100%" height="1200" src="${url}" title="${title}" frameborder="0" allow="web-share;fullscreen;"></iframe>`
    navigator.clipboard.writeText(code)
    // Get the snackbar DIV
    let x = document.getElementById('snackbar')
    if (x === null || x === undefined) return
    // Add the "show" class to DIV
    x.className = 'show'
    // After 3 seconds, remove the show class from DIV
    setTimeout(function () {
      let x = document.getElementById('snackbar')
      if (x === null || x === undefined) return
      x.className = x.className.replace('show', '')
    }, 3000)
  }

  const copyHtml = async () => {
    if (!documentId) return
    try {
      const html = await getDocumentHtml(documentId)
      const absoluteHtml = resolveRelativeUrls(html, getPreviewOrPublishUrl())
      await navigator.clipboard.writeText(absoluteHtml)
      ToastUtil.success('HTML copied to clipboard')
    } catch (err) {
      console.error(err)
      ToastUtil.error('Failed to copy HTML')
    }
  }

  const getPreviewOrPublishUrl = (): string => {
    // publish url override preview url
    const url = publishUrl ? publishUrl : previewUrl
    return url || ''
  }

  const onDownload = async () => {
    if (!documentId) return
    const blob = await downloadDocument(documentId)
    const blobURL = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobURL
    link.download = `website.zip`
    link.click()
    URL.revokeObjectURL(blobURL)
  }

  const onIntV1ToJson = async () => {
    if (!documentId) return
    await viewIntV1Json(documentId)
  }

  const onIntV1ToWord = async () => {
    if (!documentId) return
    await downloadIntV1AsWord(documentId, documentId)
  }

  const callUpdateDocumentByGeneratedQuestions = async () => {
    if (!documentId) return
    try {
      setIsSaving(true)
      await updateDocumentByGeneratedQuestions(documentId, generatedQuestions)
      // hide and clear questions
      setIsGenerated(false)
      setShowQuestions(false)
      setQuestions([])
      ToastUtil.success('Questions updated!')
      refreshPreview()
    } catch (err) {
      console.error(err)
    } finally {
      setIsSaving(false)
    }
  }

  const refreshPreview = () => {
    setPreviewUrl(null)
    setTimeout(() => {
      setPreviewUrl(getPreviewOrPublishUrl())
    }, 100)
  }

  const toggleGenerate = async () => {
    if (!documentId) {
      ToastUtil.error('Please upload a document first!')
      return
    }

    // hide and clear questions
    if (isGenerated) {
      setQuestions([])
      dispatch(deleteGeneratedQuestions())
      setShowQuestions(false)
      setIsGenerating(false)
      setIsGenerated(false)
      return
    }

    // generate questions
    try {
      setIsGenerating(true)
      const data: GeneratedQuestion[] = await generateQuestions(documentId, 2)
      // console.log(data)
      if (data.length > 0) {
        setQuestions(data)
        setIsGenerated(true)
        setShowQuestions(true)
        dispatch(storeGeneratedQuestions({ generatedQuestions: data }))
      } else {
        ToastUtil.error('Failed to generate questions!')
      }
    } catch (err) {
      console.error(err)
    } finally {
      setIsGenerating(false)
    }
  }

  const renderQuestionsButtonText = (): string => {
    if (isGenerated) return 'Hide questions'
    return 'Generate questions'
  }

  const isLoading = isUploading || isGenerating || isPublishing || isSaving

  const showSaveToTopicButton =
    !isPublished && showQuestions && questions.length > 0

  const getPreviewStyle = (): string => {
    return isChecked ? 'flex-1 h-[100%]' : 'h-[100%] w-full'
  }

  const getPreviewContainerStyle = (): string => {
    return previewUrl ? 'h-[70vh]' : 'h-[100%]'
  }

  const renderCheckerResultList = (
    title: string,
    items: LanguageToolMatch[],
  ) => {
    if (!items || items.length === 0) return <></>
    return (
      <>
        <h2 className="text-[24px] font-bold">{title}</h2>
        <ul>
          {items.map((item, index) => (
            <li
              key={index}
              className="border-sky-500 m-1 cursor-pointer border-2 border-solid p-2"
              onClick={() => scrollToHighlight(item.id)}
            >
              <div className="flex flex-col divide-y divide-dashed">
                <div className="flex flex-row text-[black]">
                  <span className="min-w-[72px]">Text:</span>
                  {/* highlight incorrect text with yellow background */}
                  <span>
                    {item.context.text
                      .split(item.incorrect)
                      .map((part, index) => (
                        <span key={index}>
                          {part}
                          {index === 0 ? (
                            <span className="bg-yellow-200">
                              {item.incorrect}
                            </span>
                          ) : (
                            item.incorrect
                          )}
                        </span>
                      ))}
                  </span>
                </div>
                <div className="flex flex-row text-[blue]">
                  <span className="min-w-[72px]">Message:</span>
                  <span>{item.message}</span>
                </div>
                <div className="flex flex-row text-[red]">
                  <span className="min-w-[72px]">Incorrect:</span>
                  <span>{item.incorrect}</span>
                </div>
                <div className="flex flex-row text-[green]">
                  <span className="min-w-[72px]">Correct:</span>
                  <span>{item.correct}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </>
    )
  }

  return (
    <SidebarLayout>
      <div className={StyleUtil.container}>
        <p className={StyleUtil.headline}>Choose a Word document to upload</p>
        <div className="mx-auto w-[96%]">
          <div className="mt-6 relative flex flex-row items-center justify-center gap-9">
            <button
              data-testid="btn-upload"
              className={StyleUtil.buttonPurple}
              onClick={() => {
                // show upload modal again
                setPreviewUrl(null)
                setShowQuestions(false)
                setMentemOrUnswBulkPage(null)
                setBulkUploadVersion(undefined)
                setMentemOrUnswPage('mentem')
                setUploadVersion(undefined)
              }}
              disabled={isLoading}
              style={{ width: '270px' }}
            >
              {isUploading && uploadingTheme === 'mentem' ? (
                <ButtonSpinner />
              ) : (
                'Create new Enterprise page'
              )}
            </button>
            <button
              data-testid="btn-upload-unsw"
              className={StyleUtil.buttonPrimaryUnsw}
              onClick={() => {
                // show upload modal again
                setPreviewUrl(null)
                setShowQuestions(false)
                setMentemOrUnswBulkPage(null)
                setBulkUploadVersion(undefined)
                setMentemOrUnswPage('unsw')
                setUploadVersion(undefined)
              }}
              disabled={isLoading}
              style={{ width: '270px' }}
            >
              {isUploading && uploadingTheme === 'unsw' ? (
                <ButtonSpinner />
              ) : (
                'Create new UNSW page'
              )}
            </button>
            <button
              data-testid="btn-upload-unsw-v2"
              className={StyleUtil.buttonPrimaryUnsw}
              onClick={() => {
                setPreviewUrl(null)
                setShowQuestions(false)
                setMentemOrUnswBulkPage(null)
                setBulkUploadVersion(undefined)
                setMentemOrUnswPage('unsw')
                setUploadVersion('2')
              }}
              disabled={isLoading}
              style={{ width: '280px' }}
            >
              {isUploading && uploadingTheme === 'unsw' ? (
                <ButtonSpinner />
              ) : (
                'Create new UNSW page (2027)'
              )}
            </button>
            <div className="absolute right-4">
              {previewUrl && (
                <Switch
                  checked={isDarkMode}
                  onChange={setIsDarkMode}
                  offLabel="Light-mode"
                  onLabel="Dark-mode"
                />
              )}
            </div>
          </div>

          <div className="mt-6 relative flex flex-row items-center justify-center gap-9">
            <button
              data-testid="btn-bulk-upload"
              className={StyleUtil.buttonPurple}
              onClick={() => {
                // clear up UI and show bulk upload UI
                setPreviewUrl(null)
                setShowQuestions(false)
                setMentemOrUnswPage(null)
                setUploadVersion(undefined)
                setMentemOrUnswBulkPage(null) // reset file selection if any
                setBulkUploadVersion(undefined)
                // render by next tick
                setTimeout(() => {
                  setMentemOrUnswBulkPage('mentem')
                }, 0)
              }}
              disabled={isLoading}
              style={{ width: '270px' }}
            >
              Bulk create Enterprise pages
            </button>
            <button
              data-testid="btn-bulk-upload-unsw"
              className={StyleUtil.buttonPrimaryUnsw}
              onClick={() => {
                // clear up UI and show bulk upload UI
                setPreviewUrl(null)
                setShowQuestions(false)
                setMentemOrUnswPage(null)
                setUploadVersion(undefined)
                setMentemOrUnswBulkPage(null) // reset file selection if any
                setBulkUploadVersion(undefined)
                // render by next tick
                setTimeout(() => {
                  setMentemOrUnswBulkPage('unsw')
                }, 0)
              }}
              disabled={isLoading}
              style={{ width: '270px' }}
            >
              Bulk create UNSW pages
            </button>
            <button
              data-testid="btn-bulk-upload-unsw-v2"
              className={StyleUtil.buttonPrimaryUnsw}
              onClick={() => {
                setPreviewUrl(null)
                setShowQuestions(false)
                setMentemOrUnswPage(null)
                setUploadVersion(undefined)
                setMentemOrUnswBulkPage(null)
                setBulkUploadVersion('2')
                setTimeout(() => {
                  setMentemOrUnswBulkPage('unsw')
                }, 0)
              }}
              disabled={isLoading}
              style={{ width: '280px' }}
            >
              Bulk create UNSW pages (2027)
            </button>
          </div>

          {previewUrl && (
            <div className="mt-3 flex flex-row items-center justify-center gap-9">
              {isPublished && (
                <button
                  data-testid="btn-copy-embed-code"
                  className={StyleUtil.buttonPrimaryUnsw}
                  onClick={copyEmbedCode}
                >
                  Copy embed code
                </button>
              )}
              {isPublished && (
                <button
                  data-testid="btn-copy-html"
                  className={StyleUtil.buttonPrimaryUnsw}
                  onClick={copyHtml}
                >
                  Copy HTML
                </button>
              )}
              <a
                href={getPreviewOrPublishUrl()}
                target="_blank"
                rel="noreferrer"
              >
                <button
                  data-testid="btn-preview"
                  className={StyleUtil.buttonPrimaryUnsw}
                >
                  Preview
                </button>
              </a>
              {isPublished && downloadUrl && (
                <button
                  data-testid="btn-download"
                  onClick={onDownload}
                  className={StyleUtil.buttonPrimaryUnsw}
                >
                  Download
                </button>
              )}
              {documentId && canPublishAuthoring && !isPublished && (
                <button
                  data-testid="btn-publish"
                  className={StyleUtil.buttonPrimaryUnsw}
                  onClick={handlePublish}
                  disabled={isLoading}
                >
                  {isPublishing && <ButtonSpinner />}
                  {!isPublishing && !isPublished && 'Publish'}
                </button>
              )}
              {documentId && (
                <>
                  <button
                    className={StyleUtil.buttonPrimaryUnsw}
                    disabled={isChecking}
                    onClick={onCheck}
                  >
                    {isChecking ? <ButtonSpinner /> : 'Check'}
                  </button>
                  <button
                    data-testid="btn-int-v1-to-word"
                    className={StyleUtil.buttonPrimaryUnsw}
                    onClick={onIntV1ToWord}
                  >
                    Word
                  </button>
                  <button
                    data-testid="btn-int-v1-to-json"
                    className={StyleUtil.buttonPrimaryUnsw}
                    onClick={onIntV1ToJson}
                  >
                    JSON
                  </button>
                  {isPublished && <span>Published!</span>}
                  {!isPublished && canPublishAuthoring && !isDryRun && (
                    <button
                      className={`${
                        isGenerated
                          ? StyleUtil.buttonBlackWhite
                          : StyleUtil.buttonPrimaryUnsw
                      }`}
                      onClick={toggleGenerate}
                      disabled={isLoading}
                    >
                      {isGenerating ? (
                        <ButtonSpinner />
                      ) : (
                        renderQuestionsButtonText()
                      )}
                    </button>
                  )}
                  <div className="grow" />

                  {showSaveToTopicButton && (
                    <button
                      data-testid="btn-save-to-topic"
                      className={`${StyleUtil.buttonPrimaryUnsw}`}
                      onClick={callUpdateDocumentByGeneratedQuestions}
                      disabled={isLoading}
                    >
                      {isSaving ? <ButtonSpinner /> : 'Save questions to topic'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {mentemOrUnswPage && !previewUrl && (
            <div className="mt-6 flex flex-row items-center justify-center gap-16">
              <article
                style={{
                  width: 560,
                  margin: '0 auto',
                  paddingTop: 36,
                  paddingLeft: 36,
                  paddingRight: 36,
                  paddingBottom: 36,
                  boxShadow:
                    '0px 570px 160px 0px rgba(0, 0, 0, 0.00), 0px 365px 146px 0px rgba(0, 0, 0, 0.01), 0px 205px 123px 0px rgba(0, 0, 0, 0.05), 0px 91px 91px 0px rgba(0, 0, 0, 0.09), 0px 23px 50px 0px rgba(0, 0, 0, 0.10)',
                }}
                aria-label="File Upload Modal"
                className="relative flex flex-col bg-white"
                onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                  e.preventDefault()
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    const droppedFile = e.dataTransfer.files[0]
                    setTheFile(droppedFile)
                  }
                }}
                onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                  if (hasFiles(e)) {
                    e.stopPropagation()
                    e.preventDefault()
                  }
                }}
                onDragEnter={(e: React.DragEvent<HTMLDivElement>) => {
                  e.preventDefault()
                  if (!hasFiles(e)) {
                    return
                  }
                }}
              >
                <section className="mx-auto flex h-full w-full flex-col overflow-hidden">
                  <header className="border-1 flex h-[270px] flex-col items-center justify-center border border-gray-400">
                    <p
                      className="flex flex-wrap justify-center"
                      style={{
                        fontWeight: 700,
                        fontSize: 20,
                      }}
                    >
                      <span>Drag file here to upload </span>
                    </p>
                    <input
                      data-testid="hidden-input"
                      id="hidden-input"
                      title="hidden-input"
                      type="file"
                      className="hidden"
                      ref={inputRef}
                      onChange={handleFileChange}
                      disabled={isLoading}
                    />
                    <button
                      data-testid="btn-select"
                      type="button"
                      id="btn-select"
                      onClick={() => inputRef.current?.click()}
                      className={`${
                        mentemOrUnswPage === 'mentem'
                          ? StyleUtil.buttonPurple
                          : StyleUtil.buttonPrimaryUnsw
                      } mb-0 mt-[60px]`}
                      disabled={isLoading}
                    >
                      <span className="pl-2 pr-2">Select file</span>
                    </button>
                  </header>
                  <div className="pt-6">Preview</div>
                  {theFile ? (
                    <div
                      style={{
                        marginTop: 6,
                        marginBottom: 24,
                      }}
                    >
                      <img
                        src={wordSvg}
                        alt={theFile.name}
                        style={{ display: 'inline-block', marginRight: 4 }}
                      />
                      <span
                        style={{
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: 16,
                          fontWeight: 700,
                          verticalAlign: 'top',
                        }}
                      >
                        {theFile.name}
                      </span>
                    </div>
                  ) : (
                    <div style={{ height: 50 }}></div>
                  )}
                </section>
                {isLoading && <Spinner />}
                <footer className="flex justify-center">
                  <button
                    data-testid="btn-submit"
                    type="button"
                    id="btn-submit"
                    onClick={() => {
                      if (!theFile) {
                        ToastUtil.warning('Please select a file')
                        return
                      }
                      // Default to mentem theme for this button, or you can add a dropdown for theme selection
                      handleUpload(mentemOrUnswPage || 'mentem')
                    }}
                    disabled={isLoading}
                    className={
                      mentemOrUnswPage === 'mentem'
                        ? StyleUtil.buttonPurple
                        : StyleUtil.buttonPrimaryUnsw + ' !w-64'
                    }
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span className="pl-2 pr-2">Upload</span>
                    {/* nasty little hack. delete asap */}
                    {mentemOrUnswPage === 'mentem' && (
                      <style>
                        {`
                        button:hover .right-arrow {
                          fill: black;
                        }
                      `}
                      </style>
                    )}

                    <RightArrowSvg
                      className="right-arrow"
                      fill={mentemOrUnswPage === 'mentem' ? 'white' : '#181716'}
                    />
                  </button>
                </footer>
              </article>
            </div>
          )}

          {mentemOrUnswBulkPage && (
            <WidgetBulkUploadTopics
              page={mentemOrUnswBulkPage}
              version={bulkUploadVersion}
              onClose={() => {
                setMentemOrUnswBulkPage(null)
                setBulkUploadVersion(undefined)
              }}
            />
          )}

          {showQuestions && documentId && questions.length > 0 && (
            <div className="authoring-questions-container mt-3">
              <div className="authoring-questions-box mentem-scrollbar-2">
                {questions.map((_question, index) => (
                  <QuestionEditViewForAuthoring
                    key={index}
                    index={index}
                    documentId={documentId}
                    locked={isPublished}
                    disabled={isLoading}
                  />
                ))}
              </div>
            </div>
          )}
          {previewUrl && (
            <div
              className={`mt-3 flex w-full flex-row gap-2 ${getPreviewContainerStyle()}`}
            >
              {spellCheck && (
                <div className="flex-1">
                  <div className="h-[100%] overflow-auto">
                    {renderCheckerResultList(
                      'Spelling',
                      spellCheck.spelling_items,
                    )}
                    {renderCheckerResultList(
                      'Grammar',
                      spellCheck.grammar_items,
                    )}
                  </div>
                </div>
              )}

              <div className={getPreviewStyle()}>
                <iframe
                  title="web-content"
                  id="content"
                  data-testid="iframe-content"
                  width={'100%'}
                  height={'100%'}
                  src={previewUrl}
                />
              </div>
            </div>
          )}

          {documentError && (
            <div dangerouslySetInnerHTML={{ __html: documentError }}></div>
          )}
          <div id="snackbar">Copied!</div>
        </div>
      </div>
    </SidebarLayout>
  )
}

export default AuthoringPage
